/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The owner row under the review's header card (design 2026-10-29): the owner rating
 * (`OwnerRating`), the most helpful note, and the asks. "Read full review" scrolls to "What owners say"
 * on this page; "See all" goes to the product page's Owners section (`location: owners`). "Add
 * your owner review" is the carousel's own `location: ask` click and opens the same in-place
 * form.
 */
import clsx from 'clsx'
import styles from './ReviewOwnersStrip.module.css'
import { openReviewBand, useReviewOwnersClick } from './ReviewOwnersCarousel'
import { OwnerRating, ratingOf, reviewsOf } from './ReviewOwnerRating'
import MemberName from 'components/community/MemberName'
import { AvatarPhoto } from 'components/community/TierAvatar'
import { avatarInitials } from 'lib/community/model'
import type { GuidePickContext } from 'lib/dealhunt/types'
import { truncateQuote } from 'lib/dealhunt/sgOwners'
import {
  GATED_REVIEW_ASK_COPY,
  noteOwnedLine,
  ownersDeepLink,
  REVIEW_ASK_COPY,
} from 'lib/owner-notes/band-copy'
import { signInGateFor } from 'lib/owner-notes/signin-gate'
import type { CardOwnersView } from 'lib/owner-notes/card-owners'
import { scrollToAnchor } from 'lib/scroll-to-anchor'

/** The line under the asks, per tone (`REVIEW_ASK_COPY.stripHint`); under the sign-in gate it
 *  ends with the requirement. */
const ASK_HINT = (signInGateFor('review') ? GATED_REVIEW_ASK_COPY : REVIEW_ASK_COPY).stripHint

interface ReviewOwnersStripProps {
  context: GuidePickContext
  /** Null (no published notes) keeps the row and its ask, with a first-review line instead. */
  owners: CardOwnersView | null
  /** Id of the "What owners say" section "Read full review" scrolls to. */
  sectionId: string
  className?: string
}

const ReviewOwnersStrip: React.VFC<ReviewOwnersStripProps> = ({
  context,
  owners,
  sectionId,
  className,
}) => {
  const onPdpClick = useReviewOwnersClick(context)
  const { note, count, verdict, tone } = ratingOf(owners)
  const ownersHref = ownersDeepLink(context.sg_href)
  let hint: keyof typeof ASK_HINT = 'neutral'
  if (verdict) {
    hint = tone
  } else if (note) {
    hint = 'early'
  }

  return (
    <aside className={clsx(styles.row, className)} data-tone={tone} aria-label="What owners say">
      <OwnerRating owners={owners} />

      <div className={styles.note}>
        {note ? (
          <>
            <span className={styles.eyebrow}>{'Most helpful owner review'}</span>
            <blockquote className={styles.quote}>
              {`${truncateQuote(note.note)} `}
              <a href={`#${sectionId}`} className={styles.more} onClick={scrollToAnchor}>
                {'Read full review →'}
              </a>
            </blockquote>
            <div className={styles.meta}>
              {/* DealHunt #218: the member's photo over the initials disc when they show one;
                  initials from the shown name, else the handle. */}
              <span className={styles.avatar} aria-hidden="true">
                {avatarInitials(note.handle, note.display_name)}
                {note.avatar_url && (
                  <AvatarPhoto key={note.avatar_url} src={note.avatar_url} px={34} />
                )}
              </span>
              <span className={styles.handle}>
                <MemberName name={note.display_name} handle={note.handle} />
              </span>
              {note.photo_status === 'verified' && (
                <span className={styles.verified}>{'Photo-verified'}</span>
              )}
              {noteOwnedLine(note) && <span className={styles.owned}>{noteOwnedLine(note)}</span>}
            </div>
          </>
        ) : (
          <>
            <span className={styles.quote}>
              {`Own the ${context.name}? Be the first to review it.`}
            </span>
            <span className={styles.owned}>{'Tell other readers how it has held up.'}</span>
          </>
        )}
      </div>

      <div className={styles.cta}>
        <a
          href={ownersHref}
          className={clsx(styles.btn, styles.primary)}
          onClick={e => {
            onPdpClick('ask', ownersHref)
            openReviewBand(e, 'strip')
          }}
        >
          {'Add your owner review →'}
        </a>
        {note ? (
          <a
            href={ownersHref}
            className={clsx(styles.btn, styles.secondary)}
            onClick={() => onPdpClick('owners', ownersHref)}
          >
            {`See all ${reviewsOf(count)}`}
          </a>
        ) : null}
        <span className={styles.hint}>{ASK_HINT[hint]}</span>
      </div>
    </aside>
  )
}

export default ReviewOwnersStrip
